import React, { useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { FaCheckCircle, FaRegEdit } from "react-icons/fa";
import { IoIosCloseCircle } from "react-icons/io";
import { LiaTrashAlt } from "react-icons/lia";
import { RiEdit2Fill } from "react-icons/ri";

import { QuestionData } from "..";
import "./style.css";
import SubmitButton from "../../../Components/Button";

interface ComplianceQuestions {
  complianceId: number;
  clauseId: number;
  topicName: string;
  policyName: string;
  questions: QuestionData[];
}

interface QuestionPreviewModalProps {
  show: boolean;
  data: ComplianceQuestions[];
  manualQuestions: any;
  onClose?: () => void;
  onEdit?: (complianceId: number, index: number, newText: string, newPurpose?: string, newArtifacts?: string) => void;
  onDelete?: (complianceId: number, index: number) => void;
  onProceed?: () => void;
}

const QuestionPreviewModal: React.FC<QuestionPreviewModalProps> = ({
  show,
  data,
  onClose,
  onEdit,
  onDelete,
  onProceed,
}) => {
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState<{
    complianceId: number;
    index: number;
  } | null>(null);

  const [editing, setEditing] = useState<{
    complianceId: number;
    index: number;
  } | null>(null);
  const [editText, setEditText] = useState("");
  const [editPurpose, setEditPurpose] = useState("");
  const [editArtifacts, setEditArtifacts] = useState("");

  /**
   * Step 1: Remove duplicate questions globally
   * Step 2: Sort by clauseId
   */
  const filteredData = useMemo(() => {
    const seenQuestions = new Set<string>();

    const result = data.map((group) => {
      const uniqueQuestions = group.questions.filter((q) => {
        const questionKey = q.questionText?.toLowerCase()?.trim();
        if (!questionKey || seenQuestions.has(questionKey)) return false;
        seenQuestions.add(questionKey);
        return true;
      });

      return { ...group, questions: uniqueQuestions };
    });

    return result.sort((a, b) => {
      const clauseA = String(a.clauseId ?? "");
      const clauseB = String(b.clauseId ?? "");
      return clauseA.localeCompare(clauseB, undefined, { numeric: true });
    });
  }, [data]);

  /**
   * Step 3: Group clauses by policy
   */
  const groupedByPolicy = useMemo(() => {
    const map = new Map<string, ComplianceQuestions[]>();

    filteredData.forEach((item) => {
      const policyKey = item.policyName || "N/A";
      if (!map.has(policyKey)) {
        map.set(policyKey, []);
      }
      map.get(policyKey)!.push(item);
    });

    return Array.from(map.entries()).map(([policyName, clauses]) => ({
      policyName,
      clauses,
    }));
  }, [filteredData]);

  const handleStartEdit = (
    complianceId: number,
    index: number,
    text: string,
    purpose?: string,
    artifacts?: string
  ) => {
    setEditing({ complianceId, index });
    setEditText(text);
    setEditPurpose(purpose || "");
    setEditArtifacts(artifacts || "");
  };

  const handleCancelEdit = () => {
    setEditing(null);
    setEditText("");
    setEditPurpose("");
    setEditArtifacts("");
  };

  const handleSaveEdit = () => {
    if (editing && onEdit) {
      onEdit(editing.complianceId, editing.index, editText, editPurpose, editArtifacts);
    }
    setEditing(null);
    setEditText("");
    setEditPurpose("");
    setEditArtifacts("");
  };

  return (
    <>
      <Modal show={show} onHide={onClose} centered size="xl">
        <Modal.Header closeButton>
          <h5 className="modalTitile">Question Preview</h5>
        </Modal.Header>

        <Modal.Body className="custom_scroll modalscroll">
          {groupedByPolicy.map((policy) => (
            <div key={policy.policyName} className="mb-4">

              {/* Policy Header (shown once) */}
              <div className="mb-3 fw-bold fs-6">
                Policy: {policy.policyName}
              </div>

              {policy.clauses.map((group) => (
                <div
                  key={group.complianceId}
                  className="mb-4 ms-3"
                >
                  {/* Clause Header */}
                  <div className="mb-2 fw-bold">
                    Clause: {group.clauseId} - {group.topicName}
                  </div>

                  {group.questions.map((question, index) => (
                    <div
                      key={index}
                      className="d-flex align-items-start mb-3"
                    >
                      {editing?.complianceId === group.complianceId &&
                        editing.index === index ? (
                        <div className="flex-grow-1 border rounded px-2 py-1">
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={editText}
                            onChange={(e) =>
                              setEditText(e.target.value)
                            }
                            className="border-0 shadow-none no-resize mb-2"
                            style={{
                              fontFamily: "inherit",
                              fontSize: "inherit",
                              color: "inherit",
                            }}
                            placeholder="Question"
                          />
                          <div className="d-flex gap-2 mb-2">
                            <div className="flex-grow-1">
                              <Form.Label className="fw-semibold mb-1" style={{ fontSize: "0.85em" }}>Purpose</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={4}
                                value={editPurpose}
                                onChange={(e) => setEditPurpose(e.target.value)}
                                className="shadow-none no-resize"
                                placeholder="Purpose (optional)"
                                style={{ fontSize: "0.9em" }}
                              />
                            </div>
                            <div className="flex-grow-1">
                              <Form.Label className="fw-semibold mb-1" style={{ fontSize: "0.85em" }}>Artifacts</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={4}
                                value={editArtifacts}
                                onChange={(e) => setEditArtifacts(e.target.value)}
                                className="shadow-none no-resize"
                                placeholder="Artifacts (optional)"
                                style={{ fontSize: "0.9em" }}
                              />
                            </div>
                          </div>
                          <div className="d-flex justify-content-end gap-2 align-items-center">
                            <IoIosCloseCircle
                              size={20}
                              className="modal-Icons Close-Icon"
                              onClick={handleCancelEdit}
                              style={{ cursor: "pointer" }}
                            />
                            <FaCheckCircle
                              size={17}
                              className="modal-Icons Check-Icon"
                              onClick={handleSaveEdit}
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          <span className="me-2 fw-semibold">
                            {index + 1}.
                          </span>

                          <span className="flex-grow-1">
                            <span className="fw-semibold">
                              {question.questionText}
                              &nbsp;&nbsp;
                              {(question as any).isCustomQuestion && (
                                <RiEdit2Fill />
                              )}
                            </span>
                            <br />

                            {question.purpose?.trim() && (
                              <>
                                <span className="text-muted">
                                  <strong>Purpose:</strong>{" "}
                                  {question.purpose}
                                </span>
                                <br />
                              </>
                            )}

                            {question.artifacts?.trim() && (
                              <span className="text-muted">
                                <strong>Artifacts:</strong>{" "}
                                {question.artifacts}
                              </span>
                            )}
                          </span>

                          <div className="d-flex gap-2 ms-2 align-items-center">
                            <FaRegEdit
                              size={18}
                              onClick={() =>
                                handleStartEdit(
                                  group.complianceId,
                                  index,
                                  question.questionText,
                                  question.purpose,
                                  question.artifacts
                                )
                              }
                              className="modal-Icons"
                              style={{ cursor: "pointer" }}
                            />
                            <LiaTrashAlt
                              size={21}
                              onClick={() => {
                                setDeleteModal(true);
                                setDeleteInfo({
                                  complianceId: group.complianceId,
                                  index,
                                });
                              }}
                              fill="#921D1D"
                              className="modal-Icons"
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </Modal.Body>

        <Modal.Footer>
          <SubmitButton
            onClick={onProceed}
          >
            Submit Selected Questions
          </SubmitButton>
        </Modal.Footer>
      </Modal>

      <Modal centered show={deleteModal} onHide={() => setDeleteModal(false)}>
        <Modal.Body className="text-center">
          <h4 className="fw-bold mb-3 text-start">Confirm Delete</h4>
          <p className="mb-4 text-start">
            Are you sure you want to delete this question?
          </p>

          <div className="d-flex justify-content-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setDeleteInfo(null);
                setDeleteModal(false);
              }}
              style={{ borderRadius: "6px", minWidth: "80px" }}
            >
              Cancel
            </Button>

            <Button
              variant="danger"
              onClick={() => {
                if (deleteInfo && onDelete) {
                  onDelete(
                    deleteInfo.complianceId,
                    deleteInfo.index
                  );
                }
                setDeleteInfo(null);
                setDeleteModal(false);
              }}
              style={{
                backgroundColor: "#6e0d0d",
                borderRadius: "6px",
                minWidth: "80px",
              }}
            >
              Confirm
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default QuestionPreviewModal;
